// Ficha 05 — Score (pista SOFOM · Carlos). Look 1:1 con el mock `wizard/05-score.html`, 4 estados:
// ready (checklist de fuentes) → consultando (spinner + sub-checks) → aprobado (hero de nivel +
// condiciones). Sin aprobación no hay pantalla de rechazo: la venta sigue sin SureKeep.
//
// INTEGRADA (guía §05 + contrato del type-kit): `POST …/score/consult` es IDEMPOTENTE por sesión y
// devuelve la decisión DIRECTA (sin polling — el contrato no tiene estado pendiente; el "Revisando…"
// dura lo que tarda el motor). `GET …/score/result` rehidrata el resultado al reanudar la sesión
// (si ya hay `loan.sciEvaluationId`). El nivel queda en `session.loan.level` para el chip de los
// pasos siguientes.
import { useState } from 'react';
import { Navigate, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, ChartLine, Loader2 } from 'lucide-react';
import { Button, Loading } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import type { AfterReferral } from '../api/useReferralFlow';
import { useScoreConsult, useScoreResult, useWizardSession } from '../api/useWizard';
import { wizardErrorCode, wizardErrorMsg } from '../api/wizardError';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardBackLink, WizardFootNav } from '../components/WizardFootNav';
import { isSureKeepAvailable } from '../sureKeepAvailability';
import type { ClientLevel, ScoreDecision } from '../types';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';

/** Hero del nivel aprobado (mock `.tier-hero-*`): pastel por nivel + badge sólido con la inicial. */
const LEVEL_META: Partial<
    Record<ClientLevel, { letter: string; name: string; hero: string; badge: string }>
> = {
    BRONZE: {
        letter: 'B',
        name: 'Bronce',
        hero: 'border-[hsl(30_60%_72%)] bg-[hsl(30_60%_92%)]',
        badge: 'bg-[hsl(24_60%_32%)] text-white',
    },
    SILVER: {
        letter: 'P',
        name: 'Plata',
        hero: 'border-[hsl(220_15%_70%)] bg-[hsl(220_15%_92%)]',
        badge: 'bg-[hsl(220_15%_32%)] text-white',
    },
    GOLD: {
        letter: 'O',
        name: 'Oro',
        hero: 'border-[hsl(45_80%_65%)] bg-[hsl(45_80%_90%)]',
        badge: 'bg-[hsl(38_60%_28%)] text-white',
    },
};

const CHECKLIST = [
    { title: 'Buró de crédito', sub: 'Revisamos su historial en Círculo de Crédito con su CURP.' },
    {
        title: 'Historial del teléfono',
        sub: 'Cómo se ha portado el número que verificamos al inicio.',
    },
    {
        title: 'Historial de compras en la tienda',
        sub: 'Si el cliente ya tenía cuenta con la tienda.',
    },
];

function CheckDot({ tone, spinning }: { tone: 'info' | 'success'; spinning?: boolean }) {
    return (
        <span
            className={cn(
                'mt-0.5 flex size-5 flex-none items-center justify-center rounded-full',
                tone === 'success' ? 'bg-success-soft text-success' : 'bg-info-soft text-info',
            )}
        >
            {spinning ? (
                <Loader2 className="size-3 animate-spin" aria-hidden />
            ) : (
                <Check className="size-3" strokeWidth={3} aria-hidden />
            )}
        </span>
    );
}

/** Lugares para las piezas de Promociones en Score; sin pieza no pasa nada. */
export interface ScoreSlots {
    /** Se dispara tras derivar una venta con extras, antes de ir al cobro. */
    afterReferral?: AfterReferral;
}

// `slots` se conserva por contrato con Promociones; sin pantalla de rechazo ya no hay derivación aquí.
export function ScoreStep({ sessionId }: { sessionId: string; slots?: ScoreSlots }) {
    const navigate = useNavigate();
    const { data: session } = useWizardSession(sessionId);
    const consult = useScoreConsult(sessionId);
    // El silo no consulta sin cuenta abierta: el vendedor regresa a Identidad a ver en qué va.
    const [accountNotReady, setAccountNotReady] = useState(false);

    // Resume: si la sesión ya tiene evaluación y no venimos de un consult en vivo, rehidrata.
    const hasSnapshot = !!session?.loan?.sciEvaluationId;
    const result = useScoreResult(sessionId, hasSnapshot && !consult.data);

    // Adelantado: llega desde el 02, antes de elegir forma de pago (D1 §wizard). El destino final es
    // el catálogo — todavía no hay pista SOFOM ni Contado decidida, así que ofrecer "cambiar a
    // Contado" o financiera externa no aplica. La autorización de buró va en los términos y
    // condiciones que el cliente acepta en el 01: aquí ya no se vuelve a pedir.
    const adelantado = session?.paymentMethodKind === null;

    if (!session) return <Loading size="lg" />;

    // El score corre UNA sola vez, adelantado (entre la cuenta y el catálogo). Con la pista SureKeep
    // ya elegida y el nivel resuelto esta pantalla repetiría el mismo resultado: sigue a la
    // cotización. Sin nivel todavía sí hay trabajo aquí — es donde se dispara la consulta.
    if (
        session.paymentMethodKind === 'SUREKEEP' &&
        session.loan?.level &&
        session.loan.level !== 'NOT_ELIGIBLE'
    ) {
        return (
            <Navigate
                to="/wizard/s/$sessionId/$step"
                params={{ sessionId, step: 'quote' }}
                replace
            />
        );
    }

    const product = session.product;
    const decision: ScoreDecision | undefined = consult.data ?? result.data;
    const consulting = consult.isPending || (hasSnapshot && !decision && result.isLoading);

    // Sin aprobación la venta sigue sin SureKeep: no hay pantalla de rechazo.
    if (!isSureKeepAvailable(session) || (decision && decision.status !== 'APPROVED')) {
        return (
            <Navigate
                to="/wizard/s/$sessionId/$step"
                params={{ sessionId, step: adelantado ? 'product' : 'payment-method' }}
                replace
            />
        );
    }

    const revisar = async (): Promise<void> => {
        setAccountNotReady(false);
        try {
            await consult.mutateAsync();
        } catch (e) {
            if (wizardErrorCode(e) === 'ACCOUNT_NOT_READY') {
                setAccountNotReady(true);
                return;
            }
            toast.error(wizardErrorMsg(e, 'No se pudo consultar el historial — intenta de nuevo.'));
        }
    };

    const continuarAlCatalogo = (): void => {
        void navigate({ to: '/wizard/s/$sessionId/$step', params: { sessionId, step: 'product' } });
    };

    const approved = decision?.status === 'APPROVED';
    const levelMeta = decision ? LEVEL_META[decision.level] : undefined;

    return (
        <WizardCard>
            <WizardCardHead
                eyebrow="Crédito"
                title="Revisamos su historial de crédito"
                sub={
                    adelantado ? (
                        'Sin costo y no afecta su historial.'
                    ) : product ? (
                        <>
                            Equipo:{' '}
                            <strong className="font-semibold text-foreground">
                                {itemDisplayName(product)}
                            </strong>{' '}
                            ·{' '}
                            <span className="tabular-nums">
                                {formatMXN(product.cashPriceCents / 100)}
                            </span>
                        </>
                    ) : undefined
                }
            />

            <WizardCardBody>
                {accountNotReady && (
                    <div
                        role="alert"
                        className="mb-4 flex items-start gap-3 rounded-lg border border-warning-soft-border bg-warning-soft p-3 text-warning"
                    >
                        <AlertTriangle className="mt-0.5 size-4 flex-none" aria-hidden />
                        <div className="min-w-0 flex-1 text-[12px] leading-relaxed">
                            La cuenta del cliente todavía no está lista. Regresa a Identidad para
                            ver en qué va.
                        </div>
                        <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<ArrowLeft className="size-3.5" aria-hidden />}
                            onClick={() =>
                                void navigate({
                                    to: '/wizard/s/$sessionId/$step',
                                    params: { sessionId, step: 'kyc' },
                                })
                            }
                        >
                            Regresar a Identidad
                        </Button>
                    </div>
                )}
                {/* ── Estado: aprobado ─────────────────────────────────────── */}
                {decision && approved ? (
                    <div>
                        <div className="mb-5 text-center">
                            <div className="mx-auto mb-3 flex size-14 items-center justify-center rounded-full bg-success-soft text-success">
                                <Check className="size-7" strokeWidth={3} aria-hidden />
                            </div>
                            <div className="mb-1 text-[15px] font-semibold">
                                SureKeep aprobó al cliente
                            </div>
                            <div className="mx-auto max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                                Este es el nivel que le toca.
                            </div>
                        </div>

                        {/* Hero del nivel */}
                        {levelMeta && (
                            <div
                                className={cn(
                                    'mb-3 flex items-center gap-3 rounded-[10px] border-[1.5px] px-4 py-3',
                                    levelMeta.hero,
                                )}
                            >
                                <span
                                    className={cn(
                                        'flex size-10 flex-none items-center justify-center rounded-full text-[16px] font-bold',
                                        levelMeta.badge,
                                    )}
                                >
                                    {levelMeta.letter}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                                        Nivel SureKeep
                                    </div>
                                    <div className="text-[16px] font-semibold leading-tight">
                                        {levelMeta.name}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* La venta usa el plan del nivel (tope, plazos, enganche), no la oferta del motor. */}
                        <div className="rounded-[10px] border border-border bg-muted/40 p-3 text-[12px] leading-relaxed text-muted-foreground">
                            Las condiciones (tope, plazos y enganche) se ven al elegir el teléfono y
                            al cotizar.
                        </div>
                    </div>
                ) : consulting ? (
                    /* ── Estado: consultando ───────────────────────────────── */
                    <div className="text-center">
                        <div className="mx-auto mb-3 flex size-14 items-center justify-center rounded-full bg-info-soft text-info">
                            <Loader2 className="size-7 animate-spin" aria-hidden />
                        </div>
                        <div className="mb-1 text-[15px] font-semibold">Revisando…</div>
                        <div className="mx-auto mb-5 max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                            En un momento tenemos el resultado. No cierres esta pantalla.
                        </div>
                        <div className="mx-auto max-w-md space-y-2 text-left text-[12.5px]">
                            <div className="flex items-center gap-3">
                                <CheckDot tone="success" />
                                <span className="font-medium">Historial de crédito revisado</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <CheckDot tone="success" />
                                <span className="font-medium">Historial del teléfono revisado</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <CheckDot tone="info" spinning />
                                <span className="font-medium">Calculando nivel SureKeep…</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* ── Estado: listo para revisar ────────────────────────── */
                    <div className="text-center">
                        <div
                            className={cn(
                                'mx-auto mb-3 flex size-14 items-center justify-center rounded-full',
                                adelantado
                                    ? 'bg-success-soft text-success'
                                    : 'bg-info-soft text-info',
                            )}
                        >
                            {adelantado ? (
                                <Check className="size-7" strokeWidth={3} aria-hidden />
                            ) : (
                                <ChartLine className="size-7" aria-hidden />
                            )}
                        </div>
                        <div className="mb-1 text-[15px] font-semibold">
                            {adelantado
                                ? 'Revisemos su historial'
                                : 'Vamos a revisar el historial del cliente'}
                        </div>
                        <div className="mx-auto mb-5 max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                            {adelantado
                                ? 'Toma un minuto y define su nivel SureKeep.'
                                : 'Toma como 1 minuto. Con el resultado se decide el nivel del cliente y las condiciones de su crédito.'}
                        </div>
                        <div className="mx-auto max-w-md space-y-2 text-left">
                            {CHECKLIST.map((item) => (
                                <div
                                    key={item.title}
                                    className="flex items-start gap-3 text-[12.5px]"
                                >
                                    <CheckDot tone="info" />
                                    <div>
                                        <div className="font-medium">{item.title}</div>
                                        <div className="text-muted-foreground">{item.sub}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </WizardCardBody>

            <WizardFootNav
                back={
                    <WizardBackLink
                        sessionId={sessionId}
                        step={adelantado ? 'kyc' : 'payment-method'}
                    />
                }
            >
                {decision && approved ? (
                    <Button
                        size="sm"
                        rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                        onClick={() => {
                            if (adelantado) {
                                continuarAlCatalogo();
                                return;
                            }
                            void navigate({
                                to: '/wizard/s/$sessionId/$step',
                                params: { sessionId, step: 'quote' },
                            });
                        }}
                    >
                        {adelantado ? 'Continuar al catálogo' : 'Continuar a cotización'}
                    </Button>
                ) : consulting ? null : (
                    <Button
                        size="sm"
                        isLoading={consult.isPending}
                        rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                        onClick={() => void revisar()}
                    >
                        Revisar historial
                    </Button>
                )}
            </WizardFootNav>
        </WizardCard>
    );
}
